import { DomainId, Langcode, type LogoThemeFragment } from '#graphql-operations'

type ComputedLogo = {
  top: string
  color: string
  bottom: string
  id?: string | null
}

type LogoOverride = Pick<ComputedLogo, 'color' | 'top'>

const COLOR_MAP: Record<string, string> = {
  '856': 'rose',
  '847': 'slate',
  '844': 'brown',
  '838': 'burgundy',
  '829': 'warning',
  '823': 'grey',
  '832': 'success',
  '820': 'sky',
  '841': 'fawn',
  '835': 'lime',
  '853': 'orange',
  '850': 'pink',
  '826': 'danger',
  '811': 'black',
  '817': 'violett',
  '814': 'primary',
}

const BOTTOM: Partial<Record<DomainId, Partial<Record<Langcode, string>>>> = {
  zuerich_com: {
    [Langcode.DE]: 'Zürich, Schweiz.',
    [Langcode.FR]: 'Zürich, Suisse.',
    [Langcode.IT]: 'Zürich, Svizzera.',
    [Langcode.EN]: 'Zürich, Switzerland.',
  },
  rapperswil_zuerichsee_ch: {
    [Langcode.DE]: 'Zürichsee, Schweiz.',
    [Langcode.EN]: 'Zürichsee, Switzerland.',
    [Langcode.FR]: 'Zürichsee, Suisse.',
    [Langcode.IT]: 'Zürichsee, Svizzera.',
  },
  eyz: {
    [Langcode.DE]: 'Einsiedeln, Ybrig, Zürichsee.',
  },
} as const

type UseLogo = {
  setLogo: (
    title?: string | null,
    color?: string | null,
    id?: string | null,
  ) => void
  setLogoOverride: (logo?: LogoOverride | null) => void
  setLogoThemeFragment: (entity: Ref<{ logoTheme?: LogoThemeFragment }>) => void
  logo: ComputedRef<ComputedLogo>
}

export function useLogo(): UseLogo {
  const { $texts } = useEasyTexts()
  const { activeDomain } = useSiteContext()
  const { isPartyMode } = usePartyToggle()
  const langcode = useCurrentLangcodeEnum()

  const logoTitle = useState<string | null | undefined>('logoTitle', () => null)
  const logoColor = useState<string | null | undefined>('logoColor', () => null)
  const logoId = useState<string | null | undefined>('logoId', () => null)
  const logoOverride = useState<LogoOverride | null>('logoOverride', () => null)

  const topText = computed<string>(() => {
    if (logoOverride.value?.top) {
      return logoOverride.value.top
    } else if (logoTitle.value) {
      return logoTitle.value
    }

    if (activeDomain === DomainId.meeting_zuerich_com) {
      return $texts('congressCity', 'Congress City')
    } else if (activeDomain === DomainId.zt_zuerich_com) {
      return $texts('b2b', 'B2B')
    } else if (activeDomain === DomainId.hoteljobs_zuerich_ch) {
      return $texts('hoteljobs', 'Hotellerie Jobs in')
    }

    return $texts('welcome', 'Welcome')
  })

  const topColor = computed<string>(() => {
    if (isPartyMode.value) {
      return 'warning'
    } else if (logoOverride.value?.color) {
      return logoOverride.value.color
    } else if (logoColor.value) {
      return logoColor.value
    }

    return 'primary'
  })

  const bottom = computed<string>(() => {
    const forDomain = BOTTOM[activeDomain] || BOTTOM.zuerich_com!
    return forDomain[langcode.value] || forDomain.DE!
  })

  const logo = computed(() => {
    return {
      top: topText.value,
      color: topColor.value,
      bottom: bottom.value,
      id: logoId.value,
    }
  })

  function setLogo(
    title?: string | null,
    color?: string | null,
    id?: string | null,
  ) {
    logoTitle.value = title
    logoColor.value = color
    logoId.value = id
  }

  function setLogoThemeFragment(
    entity: Ref<{ logoTheme?: LogoThemeFragment }>,
  ) {
    const title = entity.value.logoTheme?.top
    const id = entity.value.logoTheme?.id
    const tid = entity.value.logoTheme?.color?.tid
    const color = tid ? COLOR_MAP[tid] : null
    setLogo(title, color, id)
  }

  function setLogoOverride(override?: LogoOverride | null) {
    logoOverride.value = override || null
  }

  return {
    setLogo,
    setLogoOverride,
    setLogoThemeFragment,
    logo,
  }
}
